// @ts-nocheck - Temporary fix for messaging service types
import { backendCommunicationService } from './backendCommunicationService';
import type { JobChat, ChatMessage, ChatAttachment } from '@/types';

class MessagingService {
  private listeners: ((chats: JobChat[]) => void)[] = [];

  constructor() {
    // WebSocket connection disabled until communication service is ready
    // Silently initialize without logging
    // backendCommunicationService.connectWebSocket();
  }

  // Subscribe to chat updates
  subscribe(callback: (chats: JobChat[]) => void) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  // Notify all listeners
  private notifyListeners(userId: string) {
    this.getChatsForUser(userId).then(chats => {
      this.listeners.forEach(listener => listener(chats));
    });
  }

  // Get chats for a user - now uses real backend API
  async getChatsForUser(userId: string): Promise<JobChat[]> {
    try {
      return await backendCommunicationService.getChatsForUser(userId);
    } catch (error: any) {
      // Handle 404 errors gracefully (service not implemented yet)
      if (error.message?.includes('404') || error.message?.includes('not found')) {
        console.warn('💬 Chat service not available yet - returning empty chat list');
        return [];
      }
      console.error('Error fetching chats:', error);
      return [];
    }
  }

  // Get specific chat by ID - now uses real backend API
  async getChatById(chatId: string, userId: string): Promise<JobChat | null> {
    try {
      return await backendCommunicationService.getChatById(chatId, userId);
    } catch (error: any) {
      // Handle 404 errors gracefully (service not implemented yet)
      if (error.message?.includes('404') || error.message?.includes('not found')) {
        console.warn('💬 Chat service not available yet - chat not found');
        return null;
      }
      console.error('Error fetching chat:', error);
      return null;
    }
  }

  // Create or get chat for a job between customer and provider - now uses real backend API
  async createOrGetJobChat(
    jobId: string,
    jobTitle: string,
    customerId: string,
    customerName: string,
    providerId: string,
    providerName: string,
    providerBusinessName: string
  ): Promise<JobChat> {
    try {
      const conversation = await backendCommunicationService.createOrGetConversation(jobId, providerId);
      
      // Fetch messages for the conversation
      let messages: ChatMessage[] = [];
      try {
        messages = await backendCommunicationService.getConversationMessages(conversation.id);
      } catch (msgError) {
        console.warn('Failed to fetch messages for new conversation (non-critical):', msgError);
        // Continue with empty messages array - messages will be loaded when chat is opened
      }
      
      // Map backend conversation to JobChat format
      const chat: JobChat = {
        id: conversation.id,
        jobId: conversation.job_id,
        jobTitle,
        customerId: conversation.customer_id,
        customerName,
        providerId: conversation.provider_id,
        providerName,
        providerBusinessName,
        messages: messages || [],
        status: conversation.status === 'active' ? 'active' : 'archived',
        unreadCount: conversation.unread_count || 0,
        createdAt: new Date(conversation.created_at),
        updatedAt: new Date(conversation.updated_at)
      };

      // Notify listeners
      this.notifyListeners(customerId);
      this.notifyListeners(providerId);

      return chat;
    } catch (error) {
      console.error('Error creating/getting job chat:', error);
      throw error;
    }
  }

  // Send a message - now uses real backend API
  async sendMessage(
    chatId: string,
    senderId: string,
    senderName: string,
    senderRole: 'customer' | 'provider',
    message: string,
    messageType: ChatMessage['messageType'] = 'text',
    attachments?: ChatMessage['attachments']
  ): Promise<ChatMessage> {
    try {
      const backendMessage = await backendCommunicationService.sendMessage(
        chatId,
        message,
        messageType
      );

      // Map backend message to ChatMessage format
      const chatMessage: ChatMessage = {
        id: backendMessage.id,
        chatId: backendMessage.chatId,
        senderId: backendMessage.senderId,
        senderName,
        senderRole,
        message: backendMessage.message,
        messageType: backendMessage.messageType,
        timestamp: backendMessage.timestamp,
        read: backendMessage.read,
        attachments
      };

      // Notify listeners
      this.notifyListeners(senderId);

      return chatMessage;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  // Mark messages as read - now uses real backend API
  async markMessagesAsRead(chatId: string, userId: string): Promise<void> {
    try {
      await backendCommunicationService.markConversationRead(chatId);
      this.notifyListeners(userId);
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  }

  // Get total unread count for user
  async getTotalUnreadCount(userId: string): Promise<number> {
    try {
      return await backendCommunicationService.getTotalUnreadCount(userId);
    } catch (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  }

  // Send system message (for quote updates, job status changes, etc.)
  async sendSystemMessage(
    chatId: string,
    message: string,
    messageType: 'quote_update' | 'system' = 'system'
  ): Promise<void> {
    try {
      await backendCommunicationService.sendMessage(chatId, message, messageType);
    } catch (error) {
      console.error('Error sending system message:', error);
    }
  }

  // Archive a chat
  async archiveChat(chatId: string, userId: string): Promise<void> {
    try {
      // Backend API would need to implement this
      console.log('Archive chat not yet implemented in backend');
      this.notifyListeners(userId);
    } catch (error) {
      console.error('Error archiving chat:', error);
    }
  }

  // Send message with file attachments - now uses real backend API
  async sendMessageWithFiles(
    chatId: string,
    senderId: string,
    senderName: string,
    senderRole: 'customer' | 'provider',
    message: string,
    files: File[]
  ): Promise<ChatMessage> {
    try {
      const backendMessage = await backendCommunicationService.sendMessageWithFiles(
        chatId,
        message,
        files
      );

      // Map backend message to ChatMessage format
      const chatMessage: ChatMessage = {
        id: backendMessage.id,
        chatId: backendMessage.chatId,
        senderId: backendMessage.senderId,
        senderName,
        senderRole,
        message: backendMessage.message,
        messageType: backendMessage.messageType,
        timestamp: backendMessage.timestamp,
        read: backendMessage.read,
        attachments: backendMessage.attachments
      };

      this.notifyListeners(senderId);
      return chatMessage;
    } catch (error) {
      console.error('Error sending message with files:', error);
      throw error;
    }
  }

  // Determine file type from MIME type
  private getFileType(mimeType: string): ChatAttachment['type'] {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    return 'document';
  }

  // Send voice message
  async sendVoiceMessage(
    chatId: string,
    senderId: string,
    senderName: string,
    senderRole: 'customer' | 'provider',
    audioBlob: Blob,
    duration: number
  ): Promise<ChatMessage> {
    try {
      // Convert blob to file for backend upload
      const audioFile = new File([audioBlob], `voice-${Date.now()}.webm`, {
        type: audioBlob.type
      });

      return await this.sendMessageWithFiles(
        chatId,
        senderId,
        senderName,
        senderRole,
        `🎤 Voice message (${duration}s)`,
        [audioFile]
      );
    } catch (error) {
      console.error('Error sending voice message:', error);
      throw error;
    }
  }

  // Get all attachments from a chat
  async getChatAttachments(chatId: string): Promise<ChatAttachment[]> {
    try {
      // This would need to be implemented in the backend
      console.log('Get chat attachments not yet implemented in backend');
      return [];
    } catch (error) {
      console.error('Error getting chat attachments:', error);
      return [];
    }
  }

  // Search messages - now uses real backend API
  async searchMessages(userId: string, query: string): Promise<ChatMessage[]> {
    try {
      return await backendCommunicationService.searchMessages(userId, query);
    } catch (error) {
      console.error('Error searching messages:', error);
      return [];
    }
  }

  // Get conversation messages
  async getConversationMessages(conversationId: string): Promise<ChatMessage[]> {
    try {
      return await backendCommunicationService.getConversationMessages(conversationId);
    } catch (error) {
      console.error('Error getting conversation messages:', error);
      return [];
    }
  }
}

export const messagingService = new MessagingService();